import React, { ComponentProps } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Namespace } from '../../../translation/namespace';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useDelayed } from '../../../hooks/useDelayed';
import { Form, Input, Option, Row, Select, SonForm, SubmitButton, useForm } from '../../../_lib/Form2';
import { asView } from '../../../_lib/ViewFetcher';
import {
  CategoriesComboboxMultiSelect,
  TermSuggestComboBoxMultiSelect,
} from '../../../components/molecules/Form/components/ComboBox';
import { CopyableLink } from '../partials/CopyableLink';
import IUrl from '../../../types/IUrl';
import { RowModifier } from '../../../_lib/Form2/grid/Row/Row';

type NavigationPropertiesDefaultProps = {
  form: SonForm<any>;
  link?: IUrl;
};
export function NavigationPropertiesDefault({ form, link }: NavigationPropertiesDefaultProps) {
  const { t } = useSonTranslation('NavigationPropertiesForm');
  const { refresh, refreshCount } = useRefreshBlock();
  const delayedRefresh = useDelayed(refresh);

  const { formProps, params, fetchState } = useForm({ form, onSuccess: delayedRefresh });

  const navText = params.findByName('navText');
  const categories = params.findByName('categories');
  const keywords = params.findByName('keywords');
  const descriptivePageId = params.findByName('descriptivePageId');
  const orgUnitVisualizationType = params.findByName('orgUnitVisualizationType');

  return (
    <Form key={refreshCount} id="navigation-properties-settings-form" {...formProps}>
      {navText && (
        <Row>
          <Input label={t('inputs.navText.label')} helpText={t('inputs.navText.helpText')} param={navText} />
        </Row>
      )}
      {categories?.options?.length > 0 && (
        <Row>
          <CategoriesComboboxMultiSelect name={categories.name} value={categories.value} options={categories.options} />
        </Row>
      )}
      {keywords && (
        <Row>
          <TermSuggestComboBoxMultiSelect
            label={t(`${Namespace.Keywords}:form.label`)}
            name={keywords.name}
            suggestUrl={keywords.comboBoxUrl.url}
            maxLength={keywords.constraints.maxLength}
            value={keywords.value}
          />
        </Row>
      )}
      {descriptivePageId && (
        <Row modifier={RowModifier.BLOCK}>
          <Input
            label={t('inputs.descriptiveUrl.label')}
            helpText={t('inputs.descriptiveUrl.helptext')}
            param={descriptivePageId}
          />
          {link ? <CopyableLink to={link.url} /> : null}
        </Row>
      )}
      {orgUnitVisualizationType && (
        <Row modifier={RowModifier.BLOCK}>
          <Select
            key={orgUnitVisualizationType.value}
            label={t('inputs.orgUnitVisualizationType.label')}
            param={orgUnitVisualizationType}
          >
            {orgUnitVisualizationType.options.map((o: any) => (
              <Option key={o.value} value={o.value}>
                {t(`inputs.orgUnitVisualizationType.options.${o.value}` as any)}
              </Option>
            ))}
          </Select>
        </Row>
      )}
      <SubmitButton
        buttonLabel={t('saveChanges')}
        messageSuccess={t('status.success')}
        messageSending={t('Form:status.sending')}
        messageFail={t('status.fail')}
        active={fetchState.type === 'initial'}
        customErrorTypeMessages={{
          duplicateDescriptivePageId: t('status.duplicateDescriptivePageId'),
        }}
        fetchState={fetchState}
      />
    </Form>
  );
}

export const NavigationPropertiesDefaultView = asView(NavigationPropertiesDefault);
export type NavigationPropertiesDefaultView = ComponentProps<typeof NavigationPropertiesDefaultView>;
